import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useApolloClient } from '@apollo/react-hooks';
import { isEqual } from 'lodash';
import queryString from 'query-string';

import { FILTERS_CLEARED_QUERY_PARAM_NAME } from '../product-list-page-constants';
import usePlpNavigation from './use-plp-navigation';

import {
  getFitmentStaggeredTireProductSearch,
  getFitmentTireProductSearch
} from '../../../shared/graphql/product-list-queries';
import {
  getFitmentStaggeredWheelProductSearch,
  getFitmentWheelProductSearch,
  getPrebuiltPackageSearch,
  getStaggeredProductSearch,
  getStaggeredTirePackageSearch,
  getStaggeredWheelPackageSearch,
  getStandardProductSearch,
  getTirePackageSearch,
  getTireSizeProductSearch,
  getWheelPackageSearch,
  getWheelSizeProductSearch
} from '../graphql/plp-queries';

import {
  useDoesProductFit,
  useModal,
  useMyStore,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../../../shared/hooks/hooks';

import { clearLastPlpProduct } from '../../../shared/utils/back-to-results/back-to-results';
import {
  facetsToFilters,
  facetsToSelectedFilters,
  getNormalizedSortNames
} from '../../../shared/utils/filters/filters';
import {
  getVehicleInput,
  parseTireSize
} from '../../../shared/utils/fitment/fitment';
import {
  getNormalizedProductType,
  getSorts,
  isCanonicalProduct
} from '../../../shared/utils/product/product';
import { getTreadwellGQLInput } from '../../../shared/utils/treadwell/treadwell';
import { isArrayWithLength } from '../../../shared/utils/validators/validators';

import { TireSearchOption } from '../../../shared/types/graphql-types';
import { PLPInfoMessageIds } from '../types/plp-products';
import { DONT_FIT, EXCLUDED, FIT } from '../../../shared/constants/fit';
import { AXLE_TYPES } from '../../../shared/constants/fitment';
import {
  STEP_ONE_COMPLETE,
  VEHICLE_CONFIRMATION
} from '../../../shared/constants/modals';
import {
  ACCESSSORY_FILTER_TYPES,
  PRODUCT_TYPES,
  TREADWELL_PRODUCTS_DISPLAY_TYPES
} from '../../../shared/constants/products';
import {
  SORT_OPTIONS,
  SORT_PARAMS
} from '../../../shared/constants/search-types';
import { SetSearchTypes } from '../../../shared/constants/set-products';
import { STAGGERED_SEARCH_TYPES } from '../../../shared/constants/staggered-products';

export default function usePlpProducts({
  productType,
  queryType,
  isAccessorySearch,
  isFitmentSearch,
  isFitmentLoading,
  isSizeSearch,
  isStaggeredSearch,
  isPackageSearch,
  isPrebuiltPackageSearch,
  selectedFitment,
  baseSortValue
}) {
  const { myStore } = useMyStore();
  const { searchPosition, staggeredSearchAxle } = useParams();
  const client = useApolloClient();
  const { isTreadwellFlow, treadwellPreferences, disableTreadwellFlow } =
    useTreadwellPreferences();
  const { isOpen: isModalOpen, selectedModal } = useModal();
  const [treadwellProductsDisplayType, setTreadwellProductsDisplayType] =
    useState(TREADWELL_PRODUCTS_DISPLAY_TYPES.DEFAULT);
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS.BEST_SELLER);

  const productQueryRef = useRef();
  const { hash, pathname, search } = useLocation();
  const { q } = queryString.parse(search);

  const {
    breadcrumbs,
    category,
    clearSelectedFilters,
    filters,
    getPagenationUrl,
    initialFacets,
    initialFilters,
    metaData,
    setFilters,
    setSelectedFilters,
    selectFilters,
    selectedFilters,
    setPageNum,
    setSort,
    subtitle,
    queryParameters,
    tireSizes,
    wheelSizes
  } = usePlpNavigation(productType, queryType, selectedFitment, baseSortValue);

  const isStaggeredFitment =
    selectedFitment?.isStaggered ||
    (selectedFitment?.isDually && productType === PRODUCT_TYPES.WHEELS);
  const staggeredSearchType = useMemo(() => {
    let type;

    if (
      staggeredSearchAxle &&
      STAGGERED_SEARCH_TYPES[staggeredSearchAxle.toUpperCase()]
    ) {
      type = STAGGERED_SEARCH_TYPES[staggeredSearchAxle.toUpperCase()];
    } else if (isStaggeredFitment || isStaggeredSearch) {
      type = STAGGERED_SEARCH_TYPES.SET;
    }

    return type;
  }, [isStaggeredFitment, isStaggeredSearch, staggeredSearchAxle]);

  const { incompletePackage, isPackageInitialized } = useTireWheelPackage();
  const [packageQueryProductCodes, setPackageQueryProductCodes] = useState();
  const [hasRunInitialProductQuery, setHasRunInitialProductQuery] =
    useState(false);
  const [isLoading, setIsLoading] = useState();
  const [data, setData] = useState();
  const [error, setError] = useState();
  const [info, setInfo] = useState();
  const [allLoadedProducts, setAllLoadedProducts] = useState([]);
  const [isAllLoadedProductsLoading, setIsAllLoadedProductsLoading] =
    useState(true);
  const [searchOption, setSearchOption] = useState(TireSearchOption.TreadWell);
  const prevPageNumRef = useRef();
  const prevSearchQueryRef = useRef();
  const prevSearchTextRef = useRef();
  const prevSearchRef = useRef();
  const prevFitmentRef = useRef();
  const prevTreadwellPreferencesRef = useRef();
  const shouldLoadMoreWaitRef = useRef();

  const {
    products,
    numberOfPages,
    totalNumberOfResults,
    sorts,
    recommendationAvailable
  } = productQueryRef.current?.mapResponse(data) || {};

  const productSetSearchType = useMemo(() => {
    const categoryFilters = q?.match(/accessoryCategory:[A-Za-z]+(:)?/g);

    const isSetSearch =
      ((categoryFilters?.length === 1 &&
        categoryFilters[0]?.includes(
          ACCESSSORY_FILTER_TYPES.WINDSHIELD_WIPERS
        )) ||
        pathname?.includes('wipers-catalog')) &&
      selectedFitment;

    const setSearchType =
      SetSearchTypes[searchPosition?.toUpperCase()] || SetSearchTypes.SET;
    return isSetSearch ? setSearchType : undefined;
  }, [pathname, q, searchPosition, selectedFitment]);

  const EMPTY_PACKAGE = 'EMPTY_PACKAGE';

  const {
    query: searchQuery,
    pageNum,
    searchText,
    isSearchAll,
    sort
  } = queryParameters || {};

  const storeCode = myStore?.code;

  const onSelectSort = useCallback(
    sort => {
      clearLastPlpProduct();
      setSort(sort?.value, selectedFilters);
      setSelectedSort(getNormalizedSortNames(sort?.value));
      setTreadwellProductsDisplayType(
        TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL
      );
    },
    [selectedFilters, setSort]
  );

  function onUpdateSearchOption() {
    if (searchOption === TireSearchOption.TreadWell) {
      setSearchOption(TireSearchOption.All);
    } else {
      setSearchOption(TireSearchOption.TreadWell);
    }
    setPageNum(0);
  }

  const productQueryFn = useMemo(() => {
    if (
      (isPackageSearch && !packageQueryProductCodes) ||
      (isFitmentSearch && isFitmentLoading)
    ) {
      return;
    }

    const newProductQueryFn = (() => {
      if (isAccessorySearch) {
        return getStandardProductSearch;
      }

      if (isPrebuiltPackageSearch) {
        return getPrebuiltPackageSearch;
      }

      if (isPackageSearch) {
        if (packageQueryProductCodes === EMPTY_PACKAGE) {
          if (productType === PRODUCT_TYPES.WHEELS) {
            return isStaggeredSearch
              ? getFitmentStaggeredWheelProductSearch
              : getFitmentWheelProductSearch;
          }

          return isStaggeredSearch
            ? getFitmentStaggeredTireProductSearch
            : getFitmentTireProductSearch;
        }

        if (productType === PRODUCT_TYPES.WHEELS) {
          return isStaggeredSearch
            ? getStaggeredWheelPackageSearch
            : getWheelPackageSearch;
        }

        return isStaggeredSearch
          ? getStaggeredTirePackageSearch
          : getTirePackageSearch;
      }

      if (isSizeSearch && productType === PRODUCT_TYPES.TIRES) {
        return getTireSizeProductSearch;
      }

      if (isSizeSearch && productType === PRODUCT_TYPES.WHEELS) {
        return getWheelSizeProductSearch;
      }

      if (isFitmentSearch && productType === PRODUCT_TYPES.WHEELS) {
        if (isStaggeredSearch) {
          return getFitmentStaggeredWheelProductSearch;
        }

        return getFitmentWheelProductSearch;
      }

      if (isFitmentSearch && productType === PRODUCT_TYPES.TIRES) {
        if (isStaggeredSearch) {
          return getFitmentStaggeredTireProductSearch;
        }

        return getFitmentTireProductSearch;
      }

      if (isSearchAll) {
        return getStandardProductSearch;
      }

      if (isStaggeredSearch) {
        return getStaggeredProductSearch;
      }

      return getStandardProductSearch;
    })();

    return newProductQueryFn;
  }, [
    isAccessorySearch,
    isFitmentLoading,
    isFitmentSearch,
    isPackageSearch,
    isPrebuiltPackageSearch,
    isSearchAll,
    isSizeSearch,
    isStaggeredSearch,
    packageQueryProductCodes,
    productType
  ]);

  const productQuery = useMemo(() => {
    let treadwell = null;
    if (isTreadwellFlow) {
      if (
        productType !== PRODUCT_TYPES.TIRES ||
        (!isFitmentSearch && !isPackageSearch)
      ) {
        disableTreadwellFlow();
      } else {
        treadwell = getTreadwellGQLInput(selectedFitment, treadwellPreferences);
      }
    }
    const vehicleInput = getVehicleInput(selectedFitment);

    if (!productQueryFn) {
      return;
    }

    let frontTireSize = isSizeSearch
      ? tireSizes?.front
      : parseTireSize(vehicleInput?.frontTireSize);
    let rearTireSize = isSizeSearch
      ? tireSizes?.rear
      : parseTireSize(vehicleInput?.rearTireSize);

    let frontWheelSize = isSizeSearch
      ? wheelSizes?.front
      : vehicleInput?.frontWheelRimDiameter;
    let rearWheelSize = isSizeSearch
      ? wheelSizes?.rear
      : vehicleInput?.rearWheelRimDiameter;

    if (isSizeSearch && staggeredSearchType === AXLE_TYPES.FRONT) {
      rearTireSize = null;
      rearWheelSize = null;
    }
    if (isSizeSearch && staggeredSearchType === AXLE_TYPES.REAR) {
      frontTireSize = rearTireSize;
      rearTireSize = null;
      frontWheelSize = rearWheelSize;
      rearWheelSize = null;
    }

    return productQueryFn({
      frontTireProductCode: packageQueryProductCodes?.front,
      frontTireSize,
      frontWheelProductCode: packageQueryProductCodes?.front,
      frontWheelSize,
      initialFacets,
      initialFilters,
      pageNum,
      productSetSearchType: productSetSearchType,
      productType,
      queryType,
      rearTireProductCode: packageQueryProductCodes?.rear,
      rearTireSize,
      rearWheelProductCode: packageQueryProductCodes?.rear,
      rearWheelSize,
      searchOption:
        treadwell && searchOption === TireSearchOption.TreadWell
          ? TireSearchOption.TreadWell
          : TireSearchOption.All,
      searchQuery,
      searchText,
      selectedSort,
      shouldApplyFallback: !isFitmentSearch,
      staggeredSearchType,
      staggeredState: isStaggeredSearch ? 'ON' : 'OFF',
      storeCode,
      treadwell: searchOption === TireSearchOption.TreadWell ? treadwell : null,
      vehicleInput: !isSearchAll ? vehicleInput : undefined
    });
  }, [
    disableTreadwellFlow,
    initialFacets,
    initialFilters,
    isFitmentSearch,
    isPackageSearch,
    isSearchAll,
    isSizeSearch,
    isStaggeredSearch,
    isTreadwellFlow,
    packageQueryProductCodes,
    pageNum,
    productQueryFn,
    productSetSearchType,
    productType,
    queryType,
    searchQuery,
    searchText,
    selectedFitment,
    selectedSort,
    staggeredSearchType,
    storeCode,
    tireSizes,
    treadwellPreferences,
    wheelSizes,
    searchOption
  ]);

  const fetchProducts = useCallback(async () => {
    // ensure that there is only one fetch in flight for a single query

    if (
      !productQuery ||
      (productQuery.query === productQueryRef.current?.query &&
        isEqual(productQuery.variables, productQueryRef.current?.variables))
    ) {
      return;
    }

    productQueryRef.current = productQuery;
    shouldLoadMoreWaitRef.current = true;
    setIsAllLoadedProductsLoading(true);
    setIsLoading(true);
    setError();
    setInfo();

    let { data, error } = await client.query({ ...productQuery });
    let info;
    let { filters, products, recommendationAvailable } =
      productQuery?.mapResponse(data) || {};

    if (data) {
      if (!products && productQuery.variables?.search?.freeText) {
        const withoutVehicleProductQuery = getStandardProductSearch({
          initialFacets,
          initialFilters,
          pageNum,
          productType,
          queryType,
          searchQuery,
          searchText,
          selectedSort,
          storeCode
        });

        ({ data, error } = await client.query({
          ...withoutVehicleProductQuery
        }));

        /* Retry Treadwell query without the in stock filter if there were no
         * results. Ideally this would be handled by Hybris in the future
         */
      } else if (
        !isArrayWithLength(products) &&
        productQuery.variables.searchInput?.searchOption ===
          TireSearchOption.TreadWell &&
        productQuery.variables.searchInput?.rawQuery?.includes(
          'storeInStock:In Stock'
        )
      ) {
        ({ data, error } = await client.query({
          query: productQuery.query,
          variables: {
            ...productQuery.variables,
            searchInput: {
              ...productQuery.variables.searchInput,
              rawQuery: ':recommended'
            }
          }
        }));

        info = {
          id: PLPInfoMessageIds.noInStockTreadwellProducts
        };

        ({ filters, products } = productQuery?.mapResponse(data) || {});
      } else if (
        productQuery.variables.searchInput?.searchOption ===
          TireSearchOption.TreadWell &&
        !recommendationAvailable
      ) {
        info = {
          id: PLPInfoMessageIds.noTreadwellProducts
        };
      }

      setFilters(facetsToFilters(filters));
      setSelectedFilters(selectedFilters =>
        facetsToSelectedFilters(filters, selectedFilters, searchQuery)
      );
    }

    setData(data);
    setError(error);
    setInfo(info);
    setIsLoading(false);
  }, [
    productQuery,
    client,
    setFilters,
    setSelectedFilters,
    initialFacets,
    initialFilters,
    pageNum,
    productType,
    queryType,
    searchQuery,
    searchText,
    selectedSort,
    storeCode
  ]);

  const shouldShowTreadwellTopThreeProducts =
    treadwellProductsDisplayType !==
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_ALL &&
    isTreadwellFlow &&
    isArrayWithLength(products) &&
    products?.length > 3;

  const getProductsArr = useCallback(() => {
    let productsArr = products;
    if (process.env.__FF_LOAD_MORE__ === 'true') {
      productsArr = allLoadedProducts;
    }

    if (
      treadwellProductsDisplayType ===
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
    ) {
      return productsArr?.slice(0, 3);
    }

    return productsArr;
  }, [allLoadedProducts, products, treadwellProductsDisplayType]);

  const productCodes = products?.reduce((acc, product) => {
    if (
      isCanonicalProduct(product) &&
      Array.isArray(product?.aggregates?.codes)
    ) {
      return acc.concat(product.aggregates.codes);
    } else if (
      isStaggeredSearch &&
      staggeredSearchType === STAGGERED_SEARCH_TYPES.SET
    ) {
      acc.push({ front: product?.code, rear: product?.rearProduct?.code });
    } else {
      acc.push(product.code);
    }
    return acc;
  }, []);

  const { doesFitEnumMap, isLoading: isLoadingDoesFit } = useDoesProductFit({
    fitment: selectedFitment,
    isStaggered:
      isStaggeredSearch && staggeredSearchType === STAGGERED_SEARCH_TYPES.SET,
    productCodes
  });

  const doesFitEnums = Object.values(doesFitEnumMap ?? {});
  let allProductsDoesFitEnum = null;

  if (isPrebuiltPackageSearch) {
    allProductsDoesFitEnum = FIT;
  } else if (doesFitEnums?.some(entry => entry === DONT_FIT)) {
    allProductsDoesFitEnum = DONT_FIT;
  } else if (doesFitEnums?.some(entry => entry === FIT)) {
    allProductsDoesFitEnum = FIT;
  } else if (doesFitEnums?.some(entry => entry === EXCLUDED)) {
    allProductsDoesFitEnum = EXCLUDED;
  }

  const sortOptions = getSorts(sorts);
  const selectedSortFromData = Object.values(sortOptions).find(
    option => option.selected === true
  );

  const currentSort = selectedSortFromData?.code ?? sort;

  useEffect(() => {
    setHasRunInitialProductQuery(false);
  }, []);

  useEffect(() => {
    setPackageQueryProductCodes(() => {
      if (!isPackageSearch || !isPackageInitialized) {
        return null;
      }

      return incompletePackage?.product?.code &&
        getNormalizedProductType(incompletePackage.product) !== productType
        ? {
            front: incompletePackage.product.code,
            rear: incompletePackage.rearProduct?.code
          }
        : EMPTY_PACKAGE;
    });
  }, [isPackageSearch, incompletePackage, isPackageInitialized, productType]);

  useEffect(() => {
    if (
      (isFitmentSearch && isFitmentLoading) ||
      !myStore?.code ||
      !Array.isArray(initialFacets) ||
      !productQuery
    ) {
      setData();
      setError();
      setInfo();
      setIsLoading();
    } else {
      fetchProducts();
    }
  }, [
    productQuery,
    fetchProducts,
    initialFacets,
    isFitmentLoading,
    isFitmentSearch,
    myStore,
    packageQueryProductCodes,
    isPackageSearch,
    hash
  ]);

  useEffect(() => {
    if (search !== prevSearchRef.current) {
      shouldLoadMoreWaitRef.current = true;
    }
  }, [search, selectedFitment]);

  useEffect(() => {
    if (!isLoading) {
      shouldLoadMoreWaitRef.current = false;
    }
  }, [isLoading]);

  useEffect(() => {
    if (
      isModalOpen &&
      selectedModal !== VEHICLE_CONFIRMATION &&
      selectedModal !== STEP_ONE_COMPLETE
    ) {
      setIsAllLoadedProductsLoading(false);
    } else if (!isArrayWithLength(products) && !shouldLoadMoreWaitRef.current) {
      if (isArrayWithLength(allLoadedProducts)) {
        setAllLoadedProducts([]);
      }
      setIsAllLoadedProductsLoading(false);
    } else if (
      process.env.__FF_LOAD_MORE__ === 'true' &&
      products &&
      !isLoading
    ) {
      const areTreadwellPreferencesSame = isEqual(
        productQuery?.variables?.searchInput?.treadwell,
        prevTreadwellPreferencesRef.current?.variables?.searchInput?.treadwell
      );

      const isSearchAndOrFitmentSame =
        selectedFitment || prevFitmentRef.current
          ? search === prevSearchRef.current &&
            selectedFitment === prevFitmentRef.current
          : search === prevSearchRef.current;

      if (
        shouldLoadMoreWaitRef.current ||
        (isSearchAndOrFitmentSame && areTreadwellPreferencesSame)
      ) {
        return;
      }

      const productsAndPageNum = products.map(product => ({
        pageNum: pageNum,
        product
      }));

      const hasSearchOrFitmentChanged =
        (searchQuery &&
          (!prevSearchQueryRef.current ||
            prevSearchQueryRef.current !== searchQuery)) ||
        ((selectedFitment || prevFitmentRef.current) &&
          (!prevFitmentRef.current ||
            prevFitmentRef.current !== selectedFitment)) ||
        (searchText &&
          (!prevSearchTextRef.current ||
            prevSearchTextRef.current !== searchText)) ||
        search.includes(FILTERS_CLEARED_QUERY_PARAM_NAME);

      if (
        hasSearchOrFitmentChanged ||
        !areTreadwellPreferencesSame ||
        prevPageNumRef.current === undefined
      ) {
        setAllLoadedProducts(productsAndPageNum);
      } else if (prevPageNumRef.current > pageNum) {
        setAllLoadedProducts(prev => [...productsAndPageNum, ...prev]);
      } else if (prevPageNumRef.current < pageNum) {
        setAllLoadedProducts(prev => [...prev, ...productsAndPageNum]);
      }

      prevPageNumRef.current = pageNum;
      prevSearchRef.current = search;
      prevSearchQueryRef.current = searchQuery;
      prevSearchTextRef.current = searchText;
      prevFitmentRef.current = selectedFitment;
      prevTreadwellPreferencesRef.current = productQuery;
      setIsAllLoadedProductsLoading(false);
    }
  }, [
    allLoadedProducts,
    isLoading,
    isModalOpen,
    products,
    pageNum,
    search,
    searchText,
    searchQuery,
    selectedFitment,
    productQuery,
    selectedModal
  ]);

  useEffect(() => {
    setTreadwellProductsDisplayType(
      shouldShowTreadwellTopThreeProducts
        ? TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
        : treadwellProductsDisplayType
    );
  }, [shouldShowTreadwellTopThreeProducts, treadwellProductsDisplayType]);

  // necessary since the initial render of this hook does not perform a service request
  useEffect(() => {
    if (data || error) {
      setHasRunInitialProductQuery(true);
    }
  }, [data, error]);

  useEffect(() => {
    if (
      !sortOptions?.[currentSort] &&
      sortOptions?.[SORT_PARAMS.BEST_SELLER_ASC]
    ) {
      onSelectSort(sortOptions[SORT_PARAMS.BEST_SELLER_ASC]);
    }
  }, [currentSort, onSelectSort, selectedFilters, setSort, sortOptions, sorts]);

  return {
    allProducts: products,
    allProductsDoesFitEnum: isLoadingDoesFit ? null : allProductsDoesFitEnum,
    breadcrumbs,
    category,
    clearSelectedFilters,
    currentSort,
    error,
    filters,
    getPagenationUrl,
    info,
    isLoading:
      !hasRunInitialProductQuery ||
      (process.env.__FF_LOAD_MORE__ === 'true'
        ? isAllLoadedProductsLoading
        : isLoading),
    metaData,
    numberOfPages:
      treadwellProductsDisplayType ===
      TREADWELL_PRODUCTS_DISPLAY_TYPES.SHOW_THREE
        ? 0
        : numberOfPages,
    onSelectSort,
    onUpdateSearchOption,
    pageNum,
    productSetSearchType,
    products: getProductsArr(),
    recommendationAvailable,
    searchOption,
    searchText,
    selectFilters,
    selectedFilters,
    setPageNum,
    setSort,
    setTreadwellProductsDisplayType,
    sortOptions,
    staggeredSearchType,
    subtitle,
    tireSizes,
    totalNumberOfResults,
    treadwellProductsDisplayType,
    wheelSizes
  };
}
